// Central export for all mock data
export * from './exercises/exerciseDatabase'
// Mock programs removed to prevent UUID contamination
export * from './clients/mockClients'
// export * from './messages/mockMessages' // Removed - file doesn't exist
export * from './sessions/mockSessions'
export * from './progress/mockProgress'
export * from './coaches/mockCoaches'

// Dashboard aggregated data
import type { CoachDashboard, TodaysFocus, ClientOverview, RecentActivity, Deadline, UrgentItem } from '@/types'
import { mockClients, getClientsNeedingAttention } from './clients/mockClients'
// import { getUnreadMessages } from './messages/mockMessages' // Removed - file doesn't exist
import { getTodaysSessions } from './sessions/mockSessions'
import { mockCoachMetrics } from './coaches/mockCoaches'

// Generate today's focus data
export const generateTodaysFocus = (coachId: string): TodaysFocus => {
  const pendingCheckIns = mockClients.filter(() => 
    Math.random() > 0.7 // Simulate some clients having pending check-ins
  )
  
  const upcomingSessions = getTodaysSessions(coachId)
  
  const programUpdatesNeeded = mockClients.filter(client =>
    client.status === 'active' && Math.random() > 0.8 // Simulate some needing updates
  )

  const newMessages: any[] = [] // Mock messages not available
  
  const urgentItems: UrgentItem[] = [
    {
      id: 'urgent-001',
      type: 'injury-concern',
      clientId: 'client-001',
      clientName: 'Sarah Johnson',
      description: 'Reported knee discomfort during squats',
      actionRequired: 'Review form and modify program if needed',
      severity: 'high'
    }
  ]
  
  return {
    pendingCheckIns,
    upcomingSessions,
    programUpdatesNeeded,
    newMessages,
    urgentItems
  }
}

// Generate client overview data
export const generateClientOverview = (_coachId: string): ClientOverview => {
  const totalActiveClients = mockClients.filter(c => c.status === 'active').length
  const newThisWeek = mockClients.filter(c => {
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    return c.assignedDate > weekAgo
  }).length
  
  const needingAttention = getClientsNeedingAttention().length
  const progressingWell = mockClients.filter(c => 
    c.quickStats.progressTrend === 'improving' && c.quickStats.weeklyCompliance > 80
  ).length
  
  const averageComplianceRate = mockClients.reduce((sum, client) => 
    sum + client.quickStats.weeklyCompliance, 0
  ) / mockClients.length
  
  return {
    totalActiveClients,
    newThisWeek,
    needingAttention,
    progressingWell,
    averageComplianceRate
  }
}

// Generate recent activity
export const generateRecentActivity = (_coachId: string): RecentActivity[] => {
  return [
    {
      id: 'activity-001',
      type: 'workout-completed',
      clientId: 'client-001',
      clientName: 'Sarah Johnson',
      description: 'Completed Upper Body Strength workout',
      timestamp: new Date('2024-01-25T10:30:00')
    },
    {
      id: 'activity-002',
      type: 'goal-achieved',
      clientId: 'client-002',
      clientName: 'Mike Chen',
      description: 'Reached weight loss milestone: 175 lbs',
      timestamp: new Date('2024-01-25T08:15:00')
    },
    {
      id: 'activity-003',
      type: 'message-received',
      clientId: 'client-003',
      clientName: 'Emma Wilson',
      description: 'Sent progress update and questions',
      timestamp: new Date('2024-01-25T16:00:00')
    },
    {
      id: 'activity-004',
      type: 'session-booked',
      clientId: 'client-001',
      clientName: 'Sarah Johnson',
      description: 'Booked session for Friday 2pm',
      timestamp: new Date('2024-01-24T19:30:00')
    }
  ]
}

// Generate upcoming deadlines
export const generateUpcomingDeadlines = (_coachId: string): Deadline[] => {
  return [
    {
      id: 'deadline-001',
      type: 'program-review',
      clientId: 'client-001',
      clientName: 'Sarah Johnson',
      description: '12-week program review and planning',
      dueDate: new Date('2024-02-15'),
      priority: 'high'
    },
    {
      id: 'deadline-002',
      type: 'check-in-overdue',
      clientId: 'client-002',
      clientName: 'Mike Chen',
      description: 'Weekly check-in form overdue',
      dueDate: new Date('2024-01-24'),
      priority: 'medium'
    },
    {
      id: 'deadline-003',
      type: 'goal-deadline',
      clientId: 'client-003',
      clientName: 'Emma Wilson',
      description: '5K running goal target date approaching',
      dueDate: new Date('2024-05-01'),
      priority: 'medium'
    }
  ]
}

// Generate complete dashboard data
export const generateCoachDashboard = (coachId: string): CoachDashboard => {
  return {
    todaysFocus: generateTodaysFocus(coachId),
    clientOverview: generateClientOverview(coachId),
    quickStats: {
      totalClients: mockCoachMetrics.totalClients,
      weeklyActiveSessions: mockCoachMetrics.weeklyActiveSessions,
      averageClientRating: mockCoachMetrics.averageClientRating,
      responseTime: mockCoachMetrics.averageResponseTime
    },
    recentActivity: generateRecentActivity(coachId),
    upcomingDeadlines: generateUpcomingDeadlines(coachId)
  }
}

// Export the generated dashboard data
export const mockCoachDashboard = generateCoachDashboard('coach-001')

// Utility functions for filtering and searching
export const searchClients = (query: string) => {
  const lowercaseQuery = query.toLowerCase()
  return mockClients.filter(client => 
    client.name.toLowerCase().includes(lowercaseQuery) ||
    client.email.toLowerCase().includes(lowercaseQuery)
  )
}

export const filterClientsByStatus = (status: string) => {
  if (status === 'all') return mockClients
  return mockClients.filter(client => client.status === status)
}

export const getClientProgress = (clientId: string) => {
  const client = mockClients.find(c => c.id === clientId)
  if (!client) return null
  
  return {
    client,
    program: client.currentProgram,
    stats: client.quickStats,
    goals: client.goals
  }
}